import React, { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import StikiContent from './StikiContent'
import StikiButtons from './StikiButtons'

import { selectPageIsMine } from '../pages/pagesSlice'

import {
  updateStikiAsync,
  destroyStikiAsync,
  setStikiSelected,
  setStikiSelectedAsync,
  setStikiUnselected,
  setStikiDirty,
  setStikiColor,
  selectStiki,
  selectCurrentStikiId,
} from './stikisSlice'
import {
  startStikisMove,
  startResize,
  selectResizeOffset,
  setUIColor,
  selectUIColorCurrent,
  selectUIColorHasChanged,
  selectMarqueeIsSelecting,
} from '../ui/uiSlice'

import { calculateColor } from '../../helpers'
import {
  STIKI_HALO_BORDER,
  STIKI_MIN_WIDTH,
  STIKI_MIN_HEIGHT,
} from '../../constants'

const classes = {
  stiki: (stiki, width, height) => ({
    // .stiki {
    left: stiki.left_edge + 'em',
    top: stiki.top_edge + 'em',
    width: width + 'em',
    height: height + 'em',
    listStyle: 'none',
    position: 'absolute',
    cursor: 'default',
    boxShadow: '0 0.1em 0.3em rgba(0, 0, 0, 0.5)',
    borderRadius: '0.2em',
    '&:focus': {
      // .stiki:focus {
      outline: 'none',
    },
  }),
  stikiSelected: {
    // .stiki.selected {
    zIndex: 3,
    borderRadius: 0,
  },
  stikiDebugInfo: {
    // className='stikiDebugInfo'
    left: 0,
    bottom: 0,
    fontSize: '10px',
  },
  halo: (width, height, catchesMouse) => ({
    // .halo {
    position: 'absolute',
    left: '-2em',
    top: '-2em',
    backgroundColor: 'rgba(255, 20, 60, 0.25)',
    borderRadius: '1em',
    width: `${width + STIKI_HALO_BORDER}em`,
    height: `${height + STIKI_HALO_BORDER}em`,
    // The halo is a bigger target to grab a stiki by, which is the whole point of
    // it on a page I can edit. On one shared with me there is nothing to grab, so
    // it stops swallowing clicks: it sticks out past the stiki, and was covering
    // the neighbours underneath, so a selected stiki made the one below it
    // unclickable.
    ...(catchesMouse ? {} : { pointerEvents: 'none' }),
  }),
  stikiBackground: (color) => ({
    // .stikiBackground {
    backgroundColor: color,
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 'inherit',
  }),
  contentMargin: {
    // .contentMargin {
    position: 'absolute',
    left: '0.8em',
    top: '0.8em',
    right: '0.8em',
    bottom: '0.8em',
    borderWidth: 0,
  },
}

export default function Stiki({ stikiId }) {
  const stiki = useSelector(selectStiki(stikiId))
  const isMine = useSelector(selectPageIsMine(stiki.page_id))
  const currentStikiId = useSelector(selectCurrentStikiId)
  const isCurrent = stiki.id === currentStikiId
  const currentUIColor = useSelector(selectUIColorCurrent)
  const uiColorHasChanged = useSelector(selectUIColorHasChanged)
  const ignoreMouseMoves = useSelector(selectMarqueeIsSelecting)

  let resizerOffset = useSelector(selectResizeOffset)

  if (!isCurrent) {
    resizerOffset = { x: 0, y: 0 }
  }

  ///////////////////////////////////////////////
  //
  // Hooks
  //

  const dispatch = useDispatch()
  const domRef = useRef(null)

  ///////////////////////////////////////////////
  //
  // Stiki event handlers
  //

  const handleClick = (e) => {
    console.log('<Stiki/>.handleClick()')
    domRef?.current?.focus()
    if (!ignoreMouseMoves) {
      // console.log('...stopping propagation')
      e.stopPropagation()
    }
  }

  const handleMouseDown = (e) => {
    // console.log('<Stiki/>.handleStikiMouseDown()')

    // On a page someone shared with me, clicking still picks a stiki out: it gets
    // the selection halo and renders above the others while it is selected, which
    // is what you want for reading one that is overlapped. `raise: false` keeps
    // that to the selection — without it the stiki would keep its new place in
    // the pile after the selection cleared, and the changed `z_index` would mark
    // it dirty, so the next deselect would try to save someone else's stiki.
    // Nothing here is saved, and `startStikisMove` below doesn't happen.
    if (!isMine) {
      if (!stiki.isSelected) {
        dispatch(
          setStikiSelected({
            stikiId: stiki.id,
            retainSelection: false,
            raise: false,
          })
        )
        // Keeps the colour picker in step with the stiki picked, so a colour
        // pick starts from the stiki's own colour. Picker state is local, and
        // `hasChanged: false` means nothing is treated as recoloured.
        if (stiki.color !== currentUIColor) {
          dispatch(setUIColor({ current: stiki.color, hasChanged: false }))
        }
      }
      e.preventDefault()
      e.stopPropagation()
      return
    }

    if (stiki.isSelected) {
      if (e.shiftKey) {
        dispatch(setStikiUnselected(stiki.id))
      }
    } else {
      const retainSelection = e.shiftKey
      // The async one, so that being brought to the front is saved now rather
      // than waiting for the deselect.
      dispatch(setStikiSelectedAsync({ stikiId: stiki.id, retainSelection }))
      if (stiki.color !== currentUIColor) {
        dispatch(setUIColor({ current: stiki.color, hasChanged: false }))
      }
    }
    dispatch(
      startStikisMove({
        x: e.pageX,
        y: e.pageY,
      })
    )
    e.preventDefault()
    e.stopPropagation()
  }

  const handleMouseUp = (e) => {
    console.log('<Stiki />.handleMouseUp()')
    e.preventDefault()
  }

  const stikiBackgroundHandleMouseUp = (e) => {
    console.log('<Stiki />.stikiBackgroundHandleMouseUp()')
    e.preventDefault()
    //e.stopPropagation()
  }

  const stikiContentMarginHandleMouseUp = (e) => {
    console.log('<Stiki />.stikiContentMarginHandleMouseUp()')
    e.preventDefault()
    // e.stopPropagation()
  }

  ///////////////////////////////////////////////
  //
  // Layout constants
  //

  const width = Math.max(STIKI_MIN_WIDTH, stiki.width + resizerOffset.x)
  const height = Math.max(STIKI_MIN_HEIGHT, stiki.height + resizerOffset.y)

  // A selected stiki shows the picker's colour only while one is being picked.
  // It used to show it whenever it was the current stiki, too, which went wrong
  // when a selection box made it current: that never set the picker to the
  // stiki's own colour, so it was drawn in whatever colour was picked last.
  const color =
    stiki.isSelected && uiColorHasChanged
      ? currentUIColor
      : stiki.color // hasColored ? newColor : stiki.color

  const dirtyMarker = stiki.isDirty
    ? '<span style="background: red;">*</span>'
    : ''

  const escapedText = stiki.text.replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const stikiDebugText = `<pre>${escapedText}</pre><br/>${dirtyMarker}`
  // <br />id: ${stiki.id}<br />ignoreMouse: ${ignoreMouseMoves}`

  const debugDiv = (
    <Box
      sx={classes.stikiDebugInfo}
      dangerouslySetInnerHTML={{
        __html: stikiDebugText,
      }}
    ></Box>
  )

  const tabIndex = '0'

  ///////////////////////////////////////////////
  //
  // Markup
  //

  return (
    <Box
      sx={{
        ...classes.stiki(stiki, width, height),
        ...(stiki.isSelected ? classes.stikiSelected : {}),
      }}
      ref={domRef}
      tabIndex={tabIndex}
      // Lets the system tests find a stiki by its id rather than its text.
      data-stiki-id={stiki.id}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
    >
      {stiki.isSelected && (
        <Box className='stikiHalo' sx={classes.halo(width, height, isMine)}></Box>
      )}
      <Box
        sx={classes.stikiBackground(color)}
        className='stikiBackground'
        onMouseUp={stikiBackgroundHandleMouseUp}
      >
        {stiki.isSelected && (
          <Box
            sx={classes.contentMargin}
            onMouseUp={stikiContentMarginHandleMouseUp}
          ></Box>
        )}
        <StikiContent
          stikiId={stiki.id}
          ignoreMouseMoves={ignoreMouseMoves}
          isSelected={stiki.isSelected}
          isCurrent={isCurrent}
        />
      </Box>
      {isCurrent && <StikiButtons stikiId={stikiId} />}
    </Box>
  )
}
